import {createTheme} from "@mui/material";
import * as React from "react";

declare module '@mui/material/styles' {
    export interface Palette {
        accent: PaletteColor;
        neutral: PaletteColor;
        coloredGradient: PaletteColor;
    }

    export interface PaletteOptions {
        accent?: SimplePaletteColorOptions;
        neutral?: SimplePaletteColorOptions;
        coloredGradient?: SimplePaletteColorOptions;
    }

    export interface PaletteColor {
        medium?: string;
        bright?: string;
        lr_as?: string;
        transparent?: string;
    }

    export interface SimplePaletteColorOptions {
        medium?: string;
        bright?: string;
        lr_as?: string;
        transparent?: string;
    }
}


declare module '@mui/material/index' {

    interface ButtonPropsSizeOverrides {
        smallPlus: true;
        mediumPlus: true;
        icon: true;
        iconMedium: true;
        iconLarge: true;
    }

    interface ButtonPropsColorOverrides {
        accent: true;
    }

    interface IconButtonPropsColorOverrides {
        accent: true;
    }

    interface CheckboxPropsColorOverrides {
        accent: true;
    }

    interface ChipPropsColorOverrides {
        accent: true;
    }

    interface CircularProgressPropsColorOverrides {
        accent: true;
    }

    interface SwitchPropsColorOverrides {
        accent: true;
    }

    interface TypographyPropsVariantOverrides {
        display: true;
        h1: true;
        h2: true;
        h3: true;
        h4: true;
        h5: true;
        h6: true;
        bodyLarge: true;
        body: true;
        bodySmall: true;
    }

}

export const customTheme = createTheme({
        typography: {
            fontFamily: [
                'Tahoma',
                'Raleway',
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
                '"Apple Color Emoji"',
                '"Segoe UI Emoji"',
                '"Segoe UI Symbol"',
            ].join(','),
        },
        palette: {
            mode: 'light',
            primary: {
                main: '#011e41',
                dark: '#070328',
                medium: '#011834',
                light: '#005187',
                bright: '#034397',
                transparent: 'rgba(1, 30, 65, 0.4)',
            },
            secondary: {
                main: '#00a0df',
                dark: '#014560',
                medium: '#0079A8',
                light: '#BDE2F0',
                bright: '#89DEFF',
                transparent: 'rgba(0, 160, 223, 0.4)',
            },
            accent: {
                main: "#C8017D",
                dark: '#43002A',
                medium: '#810050',
                light: '#EF63BA',
                bright: '#F418A1',
                contrastText: "#FFFFFF",
                transparent: 'rgba(200,1,125,0.4)',
            },
            neutral: {
                main: "#DBE4E9",
                dark: '#979797',
                light: '#E3E3E3',
                bright: '#F4F5F6',
                transparent: 'rgba(219, 228, 233, 0.4)',
            },
            coloredGradient: {
                main: "linear-gradient(90deg, #C8017D 0%, #00A0DF 100%)",
                lr_as: 'linear-gradient(90deg, #C8017D 0%, #00a0df 100%)'
            },
            success: {
                main: "#5DBE7B",
                transparent: 'rgba(93, 190, 123, 0.4)',
            },
            error: {
                main: "#E20B0B",
                transparent: 'rgba(226, 11, 11, 0.4)',
            },
            warning: {
                main: "#F5CF05",
                transparent: 'rgba(245, 207, 5, 0.4)',
            }
        },
        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        boxShadow: 'none'
                    }
                }
            },
            MuiAutocomplete: {
                defaultProps: {size: 'small', blurOnSelect: true},
                /*styleOverrides: {
                    root: {
                        '& .Mui-focused': {
                            position: 'absolute',
                            background: 'white',
                            zIndex: 1,
                            maxHeight: '10000px!important',
                        },
                        '& .MuiFormHelperText-root.Mui-focused': {
                            display: 'none',
                        },
                        '& .MuiInputBase-adornedStart': {
                            maxHeight: '29px',
                        }
                    },
                },*/
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        borderBottom: '1px solid #E3E3E3',
                        marginBottom: '16px !important',
                    }
                }
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#F8F8F8',
                        padding: '16px',
                    }
                }
            },
            MuiList: {
                defaultProps: {dense: true,}
            },
            MuiMenuItem: {
                defaultProps: {dense: true,}
            },
            MuiTable: {
                defaultProps: {size: 'small',}
            },
            MuiTabs: {
                styleOverrides: {
                    root: ({ownerState, theme}) => {
                        return ({
                            '& .MuiTab-root': {
                                textTransform: 'none',
                            },
                            '& .MuiTab-root.Mui-selected': {
                                borderBottom: '3px solid ' + theme.palette.accent.main,
                                zIndex: 1,
                            }
                        });
                    },
                }
            },
            MuiButton: {
                defaultProps: {
                    size: 'small',
                    variant: 'contained',
                    color: 'accent'
                },
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                    }
                },
                variants: [
                    {
                        props: {variant: "contained", size: "icon"},
                        style: {padding: "0px", height: "28px", minWidth: "28px"}
                    },
                    {
                        props: {variant: "contained", size: "iconMedium"},
                        style: {padding: "0px", height: "40px", minWidth: "40px"}
                    },
                    {
                        props: {variant: "contained", size: "iconLarge"},
                        style: {padding: "0px", height: "56px", minWidth: "56px"}
                    },
                    {
                        props: {variant: "contained", size: "small"},
                        style: {padding: "0px 16px", height: "28px", minWidth: "121px"}
                    },
                    {
                        props: {variant: "contained", size: "smallPlus"},
                        style: {padding: "0px 16px", height: "38px", minWidth: "190px"}
                    },
                    {
                        props: {variant: "contained", size: "medium"},
                        style: {padding: "0px 16px", height: "48px", minWidth: "256px"}
                    },
                    {
                        props: {variant: "contained", size: "mediumPlus"},
                        style: {padding: "0px 16px", height: "52px", minWidth: "300px"}
                    },
                    {
                        props: {variant: "contained", size: "large"},
                        style: {padding: "0px 16px", height: "56px", minWidth: "343px"}
                    },
                    {
                        props: {variant: "outlined", size: "icon"},
                        style: {padding: "0px", height: "28px", minWidth: "28px"}
                    },
                    {
                        props: {variant: "outlined", size: "iconMedium"},
                        style: {padding: "0px", height: "40px", minWidth: "40px"}
                    },
                    {
                        props: {variant: "outlined", size: "iconLarge"},
                        style: {padding: "0px", height: "56px", minWidth: "56px"}
                    },
                    {
                        props: {variant: "outlined", size: "small"},
                        style: {padding: "0px 16px", height: "28px", minWidth: "121px"}
                    },
                    {
                        props: {variant: "outlined", size: "smallPlus"},
                        style: {padding: "0px 16px", height: "38px", minWidth: "190px"}
                    },
                    {
                        props: {variant: "outlined", size: "medium"},
                        style: {padding: "0px 16px", height: "48px", minWidth: "256px"}
                    },
                    {
                        props: {variant: "outlined", size: "mediumPlus"},
                        style: {padding: "0px 16px", height: "52px", minWidth: "300px"}
                    },
                    {
                        props: {variant: "outlined", size: "large"},
                        style: {padding: "0px 16px", height: "56px", minWidth: "343px"}
                    },
                    {
                        props: {variant: "text", size: "icon"},
                        style: {padding: "0px", height: "28px", minWidth: "28px"}
                    },
                    {
                        props: {variant: "text", size: "iconMedium"},
                        style: {padding: "0px", height: "40px", minWidth: "40px"}
                    },
                    {
                        props: {variant: "text", size: "iconLarge"},
                        style: {padding: "0px", height: "56px", minWidth: "56px"}
                    },
                    {
                        props: {variant: "text", size: "small"},
                        style: {padding: "0px 16px", height: "28px", minWidth: "121px"}
                    },
                    {
                        props: {variant: "text", size: "smallPlus"},
                        style: {padding: "0px 16px", height: "38px", minWidth: "190px"}
                    },
                    {
                        props: {variant: "text", size: "medium"},
                        style: {padding: "0px 16px", height: "48px", minWidth: "256px"}
                    },
                    {
                        props: {variant: "text", size: "mediumPlus"},
                        style: {padding: "0px 16px", height: "52px", minWidth: "300px"}
                    },
                    {
                        props: {variant: "text", size: "large"},
                        style: {padding: "0px 16px", height: "56px", minWidth: "343px"}
                    }
                ],
            },
            MuiButtonGroup: {
                defaultProps: {size: 'small',}
            },
            MuiCheckbox: {
                defaultProps: {size: 'small',},
            },
            MuiChip: {
                defaultProps: {size: 'small',},
                variants: [
                    /*{
                        props: {variant: "outlined"},
                        style: ({theme}) => ({
                            color: theme.palette.primary.main
                        })
                    },*/
                    {
                        props: {variant: "filled", color: "default"},
                        style: ({theme}) => ({
                            color: theme.palette.primary.main,
                            backgroundColor: theme.palette.neutral.light,
                        })
                    },/*{
                        props: {variant: "filled", color: "primary"},
                        style: ({theme}) => ({
                            color: theme.palette.primary.main,
                            backgroundColor: theme.palette.primary.transparent,
                        })
                    },*/{
                        props: {variant: "filled", color: "secondary"},
                        style: ({theme}) => ({
                            color: theme.palette.primary.main,
                            backgroundColor: theme.palette.secondary.transparent,
                        })
                    }, {
                        props: {variant: "filled", color: "accent"},
                        style: ({theme}) => ({
                            color: theme.palette.primary.main,
                            backgroundColor: theme.palette.accent.transparent,
                        })
                    }, {
                        props: {variant: "filled", color: "success"},
                        style: ({theme}) => ({
                            color: theme.palette.primary.main,
                            backgroundColor: theme.palette.success.transparent,
                        })
                    }, {
                        props: {variant: "filled", color: "error"},
                        style: ({theme}) => ({
                            color: theme.palette.primary.main,
                            backgroundColor: theme.palette.error.transparent,
                        })
                    }, {
                        props: {variant: "filled", color: "warning"},
                        style: ({theme}) => ({
                            color: theme.palette.primary.main,
                            backgroundColor: theme.palette.warning.transparent,
                        })
                    },
                ],
            },
            MuiCircularProgress: {
                styleOverrides: {
                    circle: {
                        stroke: 'url("#circularProgressGradient")'
                    },
                }
            },
            /*MuiCalendarPicker: {
                styleOverrides: {
                    root: ({ownerState, theme}) => {
                        return ({
                            '& .MuiPickersArrowSwitcher-root': {
                                '& .MuiSvgIcon-root': {
                                    color: theme.palette.accent.main,
                                }
                            },
                            '& .MuiPickersDay-today': {
                                borderColor: theme.palette.accent.main,
                            },
                            '& .MuiPickersDay-root.Mui-selected': {
                                backgroundColor: theme.palette.accent.main,
                            }
                        });
                    },
                }
            },
            MuiClockPicker: {
                styleOverrides: {
                    root: ({ownerState, theme}) => {
                        return ({
                            '& .MuiPickersArrowSwitcher-root': {
                                '& .MuiSvgIcon-root': {
                                    color: theme.palette.accent.main,
                                }
                            },
                            '& .MuiPickersDay-today': {
                                borderColor: theme.palette.accent.main,
                            },
                            '& .MuiPickersDay-root.Mui-selected': {
                                backgroundColor: theme.palette.accent.main,
                            }
                        });
                    },
                }
            },*/
            MuiFab: {
                defaultProps: {size: 'small',}
            },
            MuiFilledInput: {
                defaultProps: {
                    margin: 'dense',
                },
            },
            MuiFormControl: {
                defaultProps: {
                    margin: 'dense',
                    size: 'small',
                }
            },
            MuiFormHelperText: {
                defaultProps: {margin: 'dense',},
            },
            MuiIconButton: {
                defaultProps: {size: 'small',},
            },
            MuiInputBase: {
                defaultProps: {margin: 'dense',}
            },
            MuiInputLabel: {
                defaultProps: {margin: 'dense',}
            },
            MuiListItem: {
                defaultProps: {
                    dense: true,
                },
            },
            MuiOutlinedInput: {
                defaultProps: {
                    margin: 'dense',
                },
            },
            MuiRadio: {
                defaultProps: {size: 'small',}
            },
            MuiSelect: {
                defaultProps: {size: 'small',},
                /*styleOverrides: {
                    select: {
                        maxHeight: '20px!important',
                        overflow: 'auto',
                        "&::-webkit-scrollbar": {
                            width: '0px!important',
                        }
                    },

                },*/

            },
            MuiSlider: {
                styleOverrides: {
                    valueLabel: ({ownerState, theme}) => {
                        const color = theme.palette[ownerState.color || 'primary'];
                        return ({
                            borderRadius: '48px',
                            backgroundColor: color.main,
                        });
                    },
                }
            },
            MuiSnackbar: {
                defaultProps: {
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'center'
                    }
                },
                styleOverrides: {
                    root: ({ownerState, theme}) => {
                        return ({
                            minWidth: '60vw',
                        });
                    },
                }
            },
            MuiSnackbarContent: {
                styleOverrides: {
                    root: ({ownerState, theme}) => {
                        return ({
                            width: '100%',
                            borderRadius: '10px',
                            backgroundColor: theme.palette.primary.main
                        });
                    },
                }
            },
            MuiStepper: {
                styleOverrides: {
                    horizontal: ({ownerState, theme}) => {
                        return ({
                            '& .MuiStep-horizontal': {
                                borderTop: '4px solid',
                                paddingTop: '4px',
                                marginRight: '5px',
                                color: theme.palette.primary.transparent
                            },
                            '& .MuiStep-horizontal:has(svg.Mui-active)': {
                                color: theme.palette.accent.main
                            },
                            '& .MuiStepConnector-horizontal': {
                                display: 'none'
                            },
                            '& .MuiStepIcon-root': {
                                border: '2px solid lightgray',
                                borderRadius: '50%',
                                color: theme.palette.common.white,
                                fontSize: '80%'
                            },
                            '& .MuiStepIcon-root.Mui-active': {
                                border: '5px solid ' + theme.palette.accent.main,
                                fontSize: '40%',
                                color: theme.palette.accent.contrastText
                            },
                            '& .MuiStepIcon-root.Mui-completed': {
                                border: '2px solid transparent',
                                color: theme.palette.primary.main
                            },
                            '& .MuiStepLabel-label': {
                                color: theme.palette.primary.main
                            },
                            '& .MuiStepIcon-text': {
                                display: 'none'
                            },
                        });
                    },
                }
            },
            MuiSwitch: {
                defaultProps: {
                    color: 'accent'
                },
                styleOverrides: {
                    root: ({ownerState, theme}) => {
                        return ({
                            width: 48,
                            height: 24,
                            padding: 0,
                            '& .MuiSwitch-switchBase': {
                                padding: 0,
                                margin: 2,
                                transitionDuration: '300ms',
                                '&.Mui-checked': {
                                    transform: 'translateX(22px)',
                                    color: '#fff',
                                    '& + .MuiSwitch-track': {
                                        backgroundColor: theme.palette.accent.main,
                                        opacity: 1,
                                        border: 0,
                                    },
                                    '&.Mui-disabled + .MuiSwitch-track': {
                                        opacity: 0.7,
                                        backgroundColor: theme.palette.neutral.light,
                                    },
                                    '&:hover': {
                                        '& + .MuiSwitch-track': {
                                            backgroundColor: theme.palette.accent.medium,
                                            opacity: 1,
                                            border: 0,
                                        }
                                    }
                                },
                                '&.Mui-focusVisible .MuiSwitch-thumb': {
                                    color: theme.palette.accent.main,
                                    border: '6px solid #fff',
                                },
                                '&.Mui-disabled .MuiSwitch-thumb': {
                                    color: theme.palette.neutral.dark,
                                },
                                '&.Mui-disabled + .MuiSwitch-track': {
                                    opacity: 0.7,
                                    backgroundColor: theme.palette.neutral.light,
                                },
                            },
                            '& .MuiSwitch-thumb': {
                                boxSizing: 'border-box',
                                width: 20,
                                height: 20,
                            },
                            '& .MuiSwitch-track': {
                                borderRadius: 14,
                                backgroundColor: theme.palette.neutral.main,
                                opacity: 1,
                                transition: theme.transitions.create(['background-color'], {
                                    duration: 500,
                                }),
                            },
                            '&:hover': {
                                '& .MuiSwitch-track': {
                                    backgroundColor: theme.palette.neutral.dark,
                                    opacity: 1,
                                    border: 0,
                                }
                            },
                        });
                    }
                }
            },
            MuiTextField: {
                defaultProps: {
                    margin: 'dense',
                    size: 'small',
                    variant: 'standard'
                },
            },
            MuiToolbar: {
                defaultProps: {
                    variant: 'dense',
                },
                styleOverrides: {
                    root: {
                        minHeight: '48px!important'
                    }
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: ({ownerState, theme}) => {
                        return ({
                            backgroundColor: theme.palette.primary.main,
                            padding: '16px',
                            borderRadius: '8px'
                        });
                    },
                    arrow: ({ownerState, theme}) => {
                        return ({
                            color: theme.palette.primary.main
                        });
                    }
                },
            },
            MuiTypography: {
                defaultProps: {
                    color: 'primary'
                },
                variants: [
                    {
                        props: {variant: 'display'},
                        style: {
                            fontWeight: '700',
                            fontSize: '64px',
                        }
                    },
                    {
                        props: {variant: 'h1'},
                        style: {
                            fontWeight: '700',
                            fontSize: '48px',
                        }
                    },
                    {
                        props: {variant: 'h2'},
                        style: {
                            fontWeight: '700',
                            fontSize: '40px',
                        }
                    },
                    {
                        props: {variant: 'h3'},
                        style: {
                            fontWeight: '700',
                            fontSize: '32px',
                        }
                    },
                    {
                        props: {variant: 'h4'},
                        style: {
                            fontWeight: '700',
                            fontSize: '24px',
                        }
                    },
                    {
                        props: {variant: 'h5'},
                        style: {
                            fontWeight: '700',
                            fontSize: '18px',
                        }
                    },
                    {
                        props: {variant: 'h6'},
                        style: {
                            fontWeight: '700',
                            fontSize: '16px',
                        }
                    },
                    {
                        props: {variant: 'bodyLarge'},
                        style: {
                            fontWeight: '500',
                            fontSize: '18px',
                        }
                    },
                    {
                        props: {variant: 'body'},
                        style: {
                            fontWeight: '500',
                            fontSize: '16px',
                        }
                    },
                    {
                        props: {variant: 'bodySmall'},
                        style: {
                            fontWeight: '500',
                            fontSize: '14px',
                        }
                    },
                ]
            }
        },
    })
;
